import { HostToolError } from './tool-types';
import type { IGpthelpCatalogVisibilityFilter } from '../menu/gpthelp-admin-catalog';
import type {
  IGpthelpExcelColumnType,
  IGpthelpExcelSheetInput,
} from '../exports/gpthelp-excel-builder';
import {
  addDaysToDateStr,
  dateStrInTimeZone,
  zonedTimeToInstant,
} from '../gpthelp-business-day';

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const clampInt = (value: number, min: number, max: number) => {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, Math.floor(value)));
};

export const getRequiredString = (
  obj: Record<string, unknown>,
  key: string
) => {
  const value = obj[key];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new HostToolError('INVALID_ARGS', `Missing/invalid "${key}"`, {
      key,
      value,
    });
  }
  return value.trim();
};

export const getOptionalString = (
  obj: Record<string, unknown>,
  key: string
): string | null => {
  const value = obj[key];
  if (typeof value === 'undefined' || value === null) return null;
  if (typeof value !== 'string') {
    throw new HostToolError('INVALID_ARGS', `Invalid "${key}"`, { key, value });
  }
  return value.trim();
};

export const getOptionalNumber = (
  obj: Record<string, unknown>,
  key: string
): number | null => {
  const value = obj[key];
  if (typeof value === 'undefined' || value === null) return null;
  if (typeof value !== 'number' || Number.isNaN(value)) {
    throw new HostToolError('INVALID_ARGS', `Invalid "${key}"`, { key, value });
  }
  return value;
};

export const getOptionalBoolean = (
  obj: Record<string, unknown>,
  key: string
): boolean | null => {
  const value = obj[key];
  if (typeof value === 'undefined' || value === null) return null;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value === 'true') return true;
    if (value === 'false') return false;
  }
  throw new HostToolError('INVALID_ARGS', `Invalid "${key}"`, { key, value });
};

export const getOptionalStringArray = (
  obj: Record<string, unknown>,
  key: string
): string[] | null => {
  const value = obj[key];
  if (typeof value === 'undefined' || value === null) return null;
  if (!Array.isArray(value) || value.some((v) => typeof v !== 'string')) {
    throw new HostToolError('INVALID_ARGS', `Invalid "${key}"`, { key, value });
  }
  return value.map((v) => v.trim()).filter(Boolean);
};

/**
 * Shared coercion for number arrays: models sometimes send numeric strings.
 * Returns null when the value is not an array of finite numbers/numeric strings.
 */
const coerceNumberArray = (value: unknown): number[] | null => {
  if (!Array.isArray(value)) return null;
  const nums = value.map((v) => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string' && v.trim() !== '') return Number(v);
    return NaN;
  });
  if (nums.some((n) => !Number.isFinite(n))) return null;
  return nums;
};

export const getOptionalNumberArray = (
  obj: Record<string, unknown>,
  key: string,
  maxLength: number
): number[] | null => {
  const value = obj[key];
  if (typeof value === 'undefined' || value === null) return null;
  const nums = coerceNumberArray(value);
  if (nums === null || nums.length === 0 || nums.length > maxLength) {
    throw new HostToolError('INVALID_ARGS', `Invalid "${key}"`, { key, value });
  }
  return nums;
};

const CATALOG_VISIBILITY_FILTERS: IGpthelpCatalogVisibilityFilter[] = [
  'any',
  'customer_visible_now',
  'hidden_any_reason',
  'disabled',
  'hidden_in_branch',
  'outside_hours_now',
];

export const getOptionalVisibilityFilter = (
  obj: Record<string, unknown>,
  key: string
): IGpthelpCatalogVisibilityFilter | null => {
  const value = getOptionalString(obj, key);
  if (value === null) return null;
  const match = CATALOG_VISIBILITY_FILTERS.find((f) => f === value);
  if (!match) {
    throw new HostToolError('INVALID_ARGS', `Invalid "${key}"`, { key, value });
  }
  return match;
};

export const getRequiredNumberArray = (
  obj: Record<string, unknown>,
  key: string
): number[] => {
  const value = obj[key];
  const nums = coerceNumberArray(value);
  if (nums === null || !nums.length) {
    throw new HostToolError('INVALID_ARGS', `Missing/invalid "${key}"`, {
      key,
      value,
    });
  }
  return nums;
};

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const COMPACT_DATE_RE = /^\d{8}$/;

const isValidDateLabel = (label: string) => {
  const d = new Date(`${label}T00:00:00Z`);
  return !isNaN(d.getTime()) && d.toISOString().slice(0, 10) === label;
};

/**
 * Normalizes a business-date input to a yyyy-MM-dd label. Business dates are
 * timezone-less labels: yyyy-MM-dd and yyyyMMdd pass through; full ISO
 * instants resolve to their calendar date in the branch timezone.
 */
export function normalizeBusinessDateInput(params: {
  obj: Record<string, unknown>;
  key: string;
  timeZone: string;
}): string | null {
  const raw = getOptionalString(params.obj, params.key);
  if (raw === null || raw === '') return null;

  let label: string;
  if (DATE_ONLY_RE.test(raw)) {
    label = raw;
  } else if (COMPACT_DATE_RE.test(raw)) {
    label = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  } else {
    const parsed = new Date(raw);
    if (isNaN(parsed.getTime())) {
      throw new HostToolError('INVALID_ARGS', `Invalid "${params.key}"`, {
        key: params.key,
        value: raw,
      });
    }
    label = dateStrInTimeZone(parsed, params.timeZone);
  }

  if (!isValidDateLabel(label)) {
    throw new HostToolError('INVALID_ARGS', `Invalid "${params.key}"`, {
      key: params.key,
      value: raw,
    });
  }
  return label;
}

/**
 * Parses dateFrom/dateTo args into instants for services that take Dates.
 * Date-only values resolve to local midnight in the branch timezone; a
 * date-only `dateTo` means "end of that day" and resolves to the NEXT
 * midnight (the wrapped endpoints treat dateTo as exclusive).
 */
export function normalizeInstantRange(params: {
  obj: Record<string, unknown>;
  fromKey: string;
  toKey: string;
  timeZone: string;
}): { from: Date | null; to: Date | null } {
  const parseOne = (key: string, exclusiveDateOnly: boolean): Date | null => {
    const raw = getOptionalString(params.obj, key);
    if (raw === null || raw === '') return null;

    if (DATE_ONLY_RE.test(raw)) {
      const label = exclusiveDateOnly ? addDaysToDateStr(raw, 1) : raw;
      return zonedTimeToInstant(label, params.timeZone, 0);
    }

    const parsed = new Date(raw);
    if (isNaN(parsed.getTime())) {
      throw new HostToolError('INVALID_ARGS', `Invalid "${key}"`, {
        key,
        value: raw,
      });
    }
    return parsed;
  };

  const from = parseOne(params.fromKey, false);
  const to = parseOne(params.toKey, true);

  if (from && to && from.getTime() >= to.getTime()) {
    throw new HostToolError(
      'INVALID_ARGS',
      `"${params.fromKey}" must be before "${params.toKey}"`,
      { from: from.toISOString(), to: to.toISOString() }
    );
  }

  return { from, to };
}

/** Shared doc for range ends: normalizeInstantRange and normalizeStatsRange both read them this way. */
export const RANGE_END_DESCRIPTION =
  'End: a date (yyyy-MM-dd) is the last day included; an ISO instant is exclusive.';

interface IStatsRangePoint {
  /** yyyy-MM-dd in the branch timezone. */
  date: string;
  /** Local hour; null for a whole-day date. */
  hour: number | null;
  /** On the hour (no minutes/seconds). */
  exact: boolean;
}

const COMPACT_STATS_DATE_RE = /^(\d{4})(\d{2})(\d{2})(\d{2})?$/;

const parseStatsRangePoint = (
  raw: string,
  key: string,
  timeZone: string
): IStatsRangePoint => {
  const invalid = () =>
    new HostToolError('INVALID_ARGS', `Invalid "${key}"`, { key, value: raw });

  const compact = COMPACT_STATS_DATE_RE.exec(raw);
  const point: IStatsRangePoint | null = compact
    ? {
        date: `${compact[1]}-${compact[2]}-${compact[3]}`,
        hour: compact[4] === undefined ? null : Number(compact[4]),
        exact: true,
      }
    : DATE_ONLY_RE.test(raw)
      ? { date: raw, hour: null, exact: true }
      : null;
  if (point) {
    if (!isValidDateLabel(point.date) || (point.hour !== null && point.hour > 23)) {
      throw invalid();
    }
    return point;
  }

  const parsed = new Date(raw);
  if (isNaN(parsed.getTime())) throw invalid();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(parsed);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '00';
  return {
    date: `${part('year')}-${part('month')}-${part('day')}`,
    hour: Number(part('hour')),
    exact: part('minute') === '00' && part('second') === '00',
  };
};

const toStatsDateParam = (date: string, hour: number | null = null) =>
  `${date.replace(/-/g, '')}${hour === null ? '' : String(hour).padStart(2, '0')}`;

/**
 * Maps a model range (ends read like RANGE_END_DESCRIPTION) and the interval
 * a tool asks for to the stats API's from/to (yyyyMMdd or yyyyMMddHH) and
 * interval. The API reads a date `to` as the last day included (in hourly
 * mode the window ends at `to` instead), extends a multi-day hour-precision
 * `to` by 24h, and reads identical from/to as "no to": one day in auto mode,
 * two days with an explicit interval. So ranges starting on a day boundary
 * (a date, midnight or the business-day start) become whole business days;
 * a window inside one date keeps hour precision; a mid-day start spanning
 * dates keeps hour precision on both ends, like the stats pages' hour picks.
 */
export function normalizeStatsRange(params: {
  from: string;
  to: string | null;
  timeZone: string;
  /** 0 in calendar mode. */
  businessDayStartHour: number;
  /** Interval the tool would send; null or 'auto' lets the API pick it. */
  interval: string | null;
}): { from: string; to: string | null; interval: string | null } {
  const interval = params.interval === 'auto' ? null : params.interval;
  const rangeError = () =>
    new HostToolError('INVALID_ARGS', '"from" must be before "to"', {
      from: params.from,
      to: params.to,
    });
  const isDayBoundaryHour = (point: IStatsRangePoint) =>
    point.exact && (point.hour === 0 || point.hour === params.businessDayStartHour);
  const wholeDays = (first: string, last: string) => {
    if (last < first) throw rangeError();
    if (interval === 'hourly') {
      return {
        from: toStatsDateParam(first),
        to: toStatsDateParam(addDaysToDateStr(last, 1)),
        interval,
      };
    }
    return first === last
      ? { from: toStatsDateParam(first), to: null, interval: null }
      : { from: toStatsDateParam(first), to: toStatsDateParam(last), interval };
  };
  const hourWindow = (date: string, firstHour: number, lastHour: number) => {
    if (lastHour < firstHour) throw rangeError();
    if (lastHour === firstHour) {
      throw new HostToolError(
        'INVALID_ARGS',
        'Stats cannot return a single hour: use a window of at least two hours, or orders_find for exact times.',
        { from: params.from, to: params.to }
      );
    }
    return {
      from: toStatsDateParam(date, firstHour),
      to: toStatsDateParam(date, lastHour),
      interval: null,
    };
  };

  const start = parseStatsRangePoint(params.from, 'from', params.timeZone);
  if (params.to === null || params.to === '') {
    return start.hour === null || isDayBoundaryHour(start)
      ? wholeDays(start.date, start.date)
      : { from: toStatsDateParam(start.date, start.hour), to: null, interval };
  }
  const end = parseStatsRangePoint(params.to, 'to', params.timeZone);

  if (end.hour === null) {
    if (start.hour === null || isDayBoundaryHour(start)) {
      return wholeDays(start.date, end.date);
    }
    if (end.date < start.date) throw rangeError();
    if (end.date === start.date) return hourWindow(start.date, start.hour, 23);
    return {
      from: toStatsDateParam(start.date, start.hour),
      to: toStatsDateParam(interval === 'hourly' ? addDaysToDateStr(end.date, 1) : end.date),
      interval,
    };
  }

  // Last whole hour covered: an instant end on the hour is exclusive.
  const last =
    !end.exact
      ? { date: end.date, hour: end.hour }
      : end.hour > 0
        ? { date: end.date, hour: end.hour - 1 }
        : { date: addDaysToDateStr(end.date, -1), hour: 23 };
  if (start.hour !== null && last.date === start.date) {
    return hourWindow(start.date, start.hour, last.hour);
  }
  if (start.hour === null || isDayBoundaryHour(start)) {
    const lastBusinessDate =
      last.hour < params.businessDayStartHour ? addDaysToDateStr(last.date, -1) : last.date;
    return wholeDays(start.date, lastBusinessDate);
  }
  if (last.date < start.date) throw rangeError();
  return {
    from: toStatsDateParam(start.date, start.hour),
    to: toStatsDateParam(last.date, last.hour),
    interval,
  };
}

/** Positive-int page arg (default 1). */
export function getPageArg(obj: Record<string, unknown>, key = 'page'): number {
  const raw = getOptionalNumber(obj, key);
  if (raw === null) return 1;
  return clampInt(raw, 1, 100000);
}

/** Bounded limit arg with a tool-specific default/cap. */
export function getLimitArg(
  obj: Record<string, unknown>,
  params: { def: number; max: number; key?: string }
): number {
  const raw = getOptionalNumber(obj, params.key ?? 'limit');
  if (raw === null) return params.def;
  return clampInt(raw, 1, params.max);
}

/** Validates a string arg against a closed set of values. */
export function getOptionalEnum<T extends string>(
  obj: Record<string, unknown>,
  key: string,
  allowed: readonly T[]
): T | null {
  const raw = getOptionalString(obj, key);
  if (raw === null || raw === '') return null;
  const match = allowed.find((v) => v === raw);
  if (!match) {
    throw new HostToolError('INVALID_ARGS', `Invalid "${key}"`, {
      key,
      value: raw,
      allowed,
    });
  }
  return match;
}

const isExcelColumnType = (value: unknown): value is IGpthelpExcelColumnType =>
  value === 'string' ||
  value === 'number' ||
  value === 'date' ||
  value === 'datetime';

export function parseExcelSheetsOrThrow(
  value: unknown
): IGpthelpExcelSheetInput[] {
  if (!Array.isArray(value) || value.length === 0) {
    throw new HostToolError('INVALID_ARGS', 'Missing/invalid "sheets"', {
      sheets: value,
    });
  }

  return value.map((rawSheet, sheetIndex) => {
    if (!isRecord(rawSheet)) {
      throw new HostToolError(
        'INVALID_ARGS',
        `Invalid sheet at index ${sheetIndex}`,
        {
          sheet: rawSheet,
        }
      );
    }

    const name = rawSheet.name;
    if (typeof name !== 'string' || !name.trim()) {
      throw new HostToolError(
        'INVALID_ARGS',
        `Invalid sheet name at index ${sheetIndex}`,
        {
          name,
        }
      );
    }

    const columnsRaw = rawSheet.columns;
    if (!Array.isArray(columnsRaw) || columnsRaw.length === 0) {
      throw new HostToolError(
        'INVALID_ARGS',
        `Missing/invalid columns for sheet "${name}"`,
        { columns: columnsRaw }
      );
    }

    const columns = columnsRaw.map((rawCol, colIndex) => {
      if (!isRecord(rawCol)) {
        throw new HostToolError(
          'INVALID_ARGS',
          `Invalid column at index ${colIndex} in sheet "${name}"`,
          { column: rawCol }
        );
      }

      const key = rawCol.key;
      const header = rawCol.header;
      if (typeof key !== 'string' || !key.trim()) {
        throw new HostToolError(
          'INVALID_ARGS',
          `Invalid column key at index ${colIndex} in sheet "${name}"`,
          { key }
        );
      }
      if (typeof header !== 'string' || !header.trim()) {
        throw new HostToolError(
          'INVALID_ARGS',
          `Invalid column header at index ${colIndex} in sheet "${name}"`,
          { header }
        );
      }

      const typeRaw = rawCol.type;
      let type: IGpthelpExcelColumnType | undefined;
      if (typeof typeRaw !== 'undefined' && typeRaw !== null) {
        if (!isExcelColumnType(typeRaw)) {
          throw new HostToolError(
            'INVALID_ARGS',
            `Invalid column type for "${key}" in sheet "${name}"`,
            { type: typeRaw }
          );
        }
        type = typeRaw;
      }

      return {
        key: key.trim(),
        header: header.trim(),
        type,
      };
    });

    const rowsRaw = rawSheet.rows;
    if (!Array.isArray(rowsRaw)) {
      throw new HostToolError(
        'INVALID_ARGS',
        `Invalid rows for sheet "${name}"`,
        {
          rows: rowsRaw,
        }
      );
    }

    const rows = rowsRaw.map((r, rowIndex) => {
      if (!isRecord(r)) {
        throw new HostToolError(
          'INVALID_ARGS',
          `Invalid row at index ${rowIndex} in sheet "${name}"`,
          { row: r }
        );
      }
      return r;
    });

    return { name: name.trim(), columns, rows };
  });
}
